import { Stack } from "@certn/cascadia";
import {
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Space,
  Text,
  TextField,
} from "@certn/cascadia-react";
import { CascadiaJsonFormContext } from "@certn/forms";
import { SummaryItem } from "@certn/forms/src/components/SummaryItem";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms/src/wrappers/withCascadiaLayoutProps";
import { css } from "@emotion/css";
import { useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useApplication } from "../../ApplicationContext";
import { useHandleChange } from "../../utils";
import { normalizeVRN, validateVRN, VRNValidationError } from "./vrn-utils";

// TODO ATT-147: replace `vehicle_registration_number` with the canonical claim
// path emitted by the backend UK MOT schema once Subtask 2 lands and the
// applicant-portal generated-schemas are regenerated. Until then this control
// only renders if the backend ui_schema explicitly references it
// (uiTypeIs("VehicleRegistrationNumberSection")), so it is naturally gated to
// cases that include the UK MOT check.

const VRN_FIELD_PATH = "vehicle_registration_number";

function getErrorMessage(
  error: VRNValidationError,
  t: (key: string) => string
): string {
  switch (error) {
    case "REQUIRED":
      return t("UK vehicle registration number is required");
    case "TOO_SHORT":
      return t(
        "UK vehicle registration number must be at least 2 characters long"
      );
    case "TOO_LONG":
      return t(
        "UK vehicle registration number must be at most 7 characters long"
      );
    case "INVALID_CHARACTERS":
      return t(
        "UK vehicle registration number can only contain letters and numbers"
      );
  }
}

export function VehicleRegistrationNumberControl(props: CascadiaLayoutProps) {
  const { t } = useTranslation();
  const { isSummary, isSubmitting, submit } = useContext(
    CascadiaJsonFormContext
  );
  const { prevSection, navigateToSection } = useApplication();
  const handleChange = useHandleChange();

  const data = (props.data ?? {}) as { vehicle_registration_number?: string };
  const rawValue = data.vehicle_registration_number ?? "";

  const [touched, setTouched] = useState(false);

  const validationError = validateVRN(rawValue);
  const showError = touched && validationError !== null;

  if (isSummary) {
    return (
      <Stack gap="space-0">
        <SummaryItem label={t("UK Vehicle Registration Number (VRN)")}>
          {normalizeVRN(rawValue) || "—"}
        </SummaryItem>
      </Stack>
    );
  }

  const handleSubmit = () => {
    setTouched(true);
    if (validationError !== null) {
      return;
    }
    handleChange(VRN_FIELD_PATH, normalizeVRN(rawValue));
    submit();
  };

  return (
    <>
      <BaseLayout {...props} className={styles.layout}>
        <Text as="h1" style="form/page-title">
          <Trans>UK Vehicle Registration Number (VRN)</Trans>
        </Text>
        <Text style="form/body-secondary" color="text-base">
          <Trans>
            The UK number plate (VRN) for the vehicle whose MOT history we
            retrieve. Enter letters and numbers only; spaces are removed
            automatically.
          </Trans>
        </Text>
        <TextField
          label={t("UK Vehicle Registration Number (VRN)")}
          testID="vehicle-registration-number"
          value={rawValue}
          onChange={(value) => {
            handleChange(VRN_FIELD_PATH, value);
          }}
          onBlur={() => {
            setTouched(true);
            handleChange(VRN_FIELD_PATH, normalizeVRN(rawValue));
          }}
          isRequired
          isInvalid={showError}
          errors={
            showError && validationError
              ? [getErrorMessage(validationError, t)]
              : undefined
          }
        />
      </BaseLayout>
      <div className={styles.actionBar}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              testID="section-back-button"
              leftIcon="chevron_left"
              onPress={async () => navigateToSection(prevSection)}
            >
              <Trans>Back</Trans>
            </GhostButton>
          )}
          <Space />
          <PrimaryButton
            testID="section-next-step-button"
            type="button"
            onPress={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={validationError !== null}
          >
            <Trans>Next step</Trans>
          </PrimaryButton>
        </ButtonGroup>
      </div>
    </>
  );
}

const styles = {
  layout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  actionBar: css({
    paddingTop: "calc(var(--space-4) - 1px)",
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
    borderTop: "1px solid var(--border-base)",
  }),
};

VehicleRegistrationNumberControl.displayName =
  "VehicleRegistrationNumberSection";

export const VehicleRegistrationNumberControlRenderer = withCascadiaLayoutProps(
  VehicleRegistrationNumberControl
);
